"use client"

import { useState, useTransition } from "react"

import { Button } from "./Button"
import { Dialog } from "./Dialog"
import { Input } from "./Input"
import { Switch } from "./Switch"
import {
  createProjetoAction,
  updateProjetoAction,
} from "@/lib/projeto-planejamento/actions"
import type { ProjetoPlanejamento } from "@/lib/projeto-planejamento/types"

type ProjetoDialogProps = {
  open: boolean
  onClose: () => void
  projeto?: ProjetoPlanejamento
  onCreated?: (projeto: ProjetoPlanejamento) => void
  onUpdated?: (projeto: ProjetoPlanejamento) => void
}

const currentYear = () => new Date().getFullYear()

export function ProjetoDialog({
  open,
  onClose,
  projeto,
  onCreated,
  onUpdated,
}: ProjetoDialogProps) {
  const editing = projeto !== undefined
  const [pending, startTransition] = useTransition()
  const [nome, setNome] = useState(projeto?.nome ?? "")
  const [anoInicio, setAnoInicio] = useState<string>(
    String(projeto?.anoInicio ?? currentYear()),
  )
  const [anoFim, setAnoFim] = useState<string>(
    String(projeto?.anoFim ?? currentYear() + 2),
  )
  const [ativo, setAtivo] = useState(projeto?.ativo ?? true)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const reset = () => {
    setNome(projeto?.nome ?? "")
    setAnoInicio(String(projeto?.anoInicio ?? currentYear()))
    setAnoFim(String(projeto?.anoFim ?? currentYear() + 2))
    setAtivo(projeto?.ativo ?? true)
    setError(null)
    setFieldErrors({})
  }

  const handleClose = () => {
    if (pending) return
    reset()
    onClose()
  }

  const handleSubmit = () => {
    setError(null)
    setFieldErrors({})
    const inicio = Number(anoInicio)
    const fim = Number(anoFim)
    if (!Number.isInteger(inicio) || !Number.isInteger(fim)) {
      setFieldErrors({
        anoInicio: !Number.isInteger(inicio) ? "Ano deve ser inteiro." : "",
        anoFim: !Number.isInteger(fim) ? "Ano deve ser inteiro." : "",
      })
      return
    }
    startTransition(async () => {
      const result = editing
        ? await updateProjetoAction({
            id: projeto.id,
            nome,
            anoInicio: inicio,
            anoFim: fim,
            ativo,
          })
        : await createProjetoAction({
            nome,
            anoInicio: inicio,
            anoFim: fim,
            ativo,
          })
      if (!result.ok) {
        setError(result.error)
        setFieldErrors(result.fieldErrors ?? {})
        return
      }
      if (editing) onUpdated?.(result.data)
      else onCreated?.(result.data)
      reset()
      onClose()
    })
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={editing ? "Editar projeto" : "Novo projeto de planejamento"}
      description={
        editing
          ? "Ajuste nome, período ou status do projeto."
          : "Defina o nome e o intervalo de anos coberto pelo projeto."
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={handleClose} disabled={pending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} loading={pending}>
            {editing ? "Salvar alterações" : "Criar projeto"}
          </Button>
        </div>
      }
    >
      <form
        className="flex flex-col gap-3 py-2"
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        <Input
          label="Nome"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Planejamento 2026-2028"
          error={fieldErrors.nome}
          autoFocus
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Ano início"
            type="number"
            inputMode="numeric"
            value={anoInicio}
            onChange={(e) => setAnoInicio(e.target.value)}
            error={fieldErrors.anoInicio}
          />
          <Input
            label="Ano fim"
            type="number"
            inputMode="numeric"
            value={anoFim}
            onChange={(e) => setAnoFim(e.target.value)}
            error={fieldErrors.anoFim}
          />
        </div>
        <Switch
          checked={ativo}
          onCheckedChange={setAtivo}
          label="Projeto ativo"
          description={
            editing
              ? "Projetos arquivados ficam ocultos do seletor."
              : "Projetos inativos ficam ocultos do seletor."
          }
        />
        {error ? (
          <p role="alert" className="text-xs text-markv-danger-accent">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  )
}
